
import React, { useState, useEffect, useMemo } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "axios";
import { CartProduct } from "../../assets/icons/icons";

const API_BASE = "https://laravel-api.aj-mart.com";
const STORAGE_BASE = `${API_BASE}/storage/app/public/`;
const PRODUCTS_PER_PAGE = 20;

const PLACEHOLDER =
  "data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='800' height='600'><rect width='100%' height='100%' fill='%23f3f4f6'/><text x='50%' y='50%' dominant-baseline='middle' text-anchor='middle' fill='%239ca3af' font-size='20'>No Image</text></svg>";

const safeStr = (v) =>
  typeof v === "string" ? v : v == null ? "" : String(v);

const toNum = (v, fallback = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : fallback;
};

const normalizeString = (str) =>
  str ? String(str).toLowerCase().trim() : "";

const splitDesc = (text, lang) => {
  const s = safeStr(text).trim();
  if (!s) return [];

  if (lang?.startsWith("ar")) {
    return s
      .split(/[|،؛•\-–—]+|\r?\n/)
      .map((x) => x.trim())
      .filter(Boolean);
  }

  return s
    .split(/\r?\n|[|•\-–—]+/)
    .map((x) => x.trim())
    .filter(Boolean);
};

const shuffleArray = (arr) => {
  const a = [...arr];

  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }

  return a;
};

export default function SubCategories() {
  const { t, i18n } = useTranslation();
  const { subcategory } = useParams();

  const isArabic = i18n.language?.startsWith("ar");

  const pathParts = window.location.pathname
    .split("/")
    .filter(Boolean);

  const categoryFromUrl =
    pathParts.length >= 2
      ? decodeURIComponent(pathParts[pathParts.length - 2])
      : "";

  const subcategoryFromUrl =
    pathParts.length >= 1
      ? decodeURIComponent(pathParts[pathParts.length - 1])
      : "";

  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subCategories, setSubCategories] = useState([]);
  const [brands, setBrands] = useState([]);

  const [selectedCategory, setSelectedCategory] =
    useState(categoryFromUrl);

  const [selectedSubCategory, setSelectedSubCategory] =
    useState(subcategoryFromUrl);

  const [selectedBrand, setSelectedBrand] = useState("");
  const [selectedCapacity, setSelectedCapacity] = useState("");

  const [isLoading, setIsLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // Fetch all data
  useEffect(() => {
    let active = true;

    const fetchProducts = async () => {
      try {
        const { data } = await axios.get(
          `${API_BASE}/api/products`,
          { withCredentials: true }
        );

        if (!active) return;

        const list = Array.isArray(data?.products)
          ? data.products
          : Array.isArray(data?.products?.data)
          ? data.products.data
          : [];

        const normalized = list.map((p) => ({
          ...p,
          product_capacity_array: Array.isArray(
            p.product_capacity_array
          )
            ? p.product_capacity_array
            : typeof p.product_capacity_array === "string"
            ? p.product_capacity_array
                .split(",")
                .map((it) => it.trim())
            : [],
        }));

        setProducts(normalized);
      } catch (e) {
        console.error("Error fetching products:", e);
        if (active) setProducts([]);
      }
    };

    const fetchCategories = async () => {
      try {
        const { data } = await axios.get(
          `${API_BASE}/api/categories`
        );

        if (!active) return;

        const src = Array.isArray(data?.category)
          ? data.category
          : [];

        const unique = Array.from(
          new Map(
            src.map((it) => [
              normalizeString(it.category_name_E),
              it,
            ])
          ).values()
        );

        setCategories(unique);
      } catch (e) {
        console.error("Error fetching categories:", e);
        if (active) setCategories([]);
      }
    };

    const fetchSubCategories = async () => {
      try {
        const { data } = await axios.get(
          `${API_BASE}/api/subCategory`
        );

        if (!active) return;

        const src = Array.isArray(data?.sub_category)
          ? data.sub_category
          : [];

        const unique = Array.from(
          new Map(
            src.map((it) => [
              normalizeString(it.sub_category_name_E),
              it,
            ])
          ).values()
        );

        setSubCategories(unique);
      } catch (e) {
        console.error("Error fetching subcategories:", e);
        if (active) setSubCategories([]);
      }
    };

    const fetchBrands = async () => {
      try {
        const { data } = await axios.get(
          `${API_BASE}/api/brand`
        );

        if (!active) return;

        const src = Array.isArray(data?.brands)
          ? data.brands
          : [];

        const unique = Array.from(
          new Map(
            src.map((it) => [
              normalizeString(it.brand_name),
              it,
            ])
          ).values()
        );

        setBrands(unique);
      } catch (e) {
        console.error("Error fetching brands:", e);
        if (active) setBrands([]);
      }
    };

    const fetchAll = async () => {
      setIsLoading(true);

      await Promise.all([
        fetchProducts(),
        fetchCategories(),
        fetchSubCategories(),
        fetchBrands(),
      ]);

      if (active) setIsLoading(false);
    };

    fetchAll();

    return () => {
      active = false;
    };
  }, []);

  // Reset capacity for Airpods and Charger
  useEffect(() => {
    if (
      normalizeString(selectedCategory) === "airpods" ||
      normalizeString(selectedCategory) === "charger"
    ) {
      setSelectedCapacity("");
    }
  }, [selectedCategory]);

  // Sync category selection with URL
  useEffect(() => {
    if (categories.length > 0 && subCategories.length > 0) {
      const cNorm = normalizeString(categoryFromUrl);
      const sNorm = normalizeString(subcategoryFromUrl);

      const matchedC = categories.find(
        (it) =>
          normalizeString(it.category_name_E) === cNorm
      );

      const matchedS = subCategories.find(
        (it) =>
          normalizeString(it.sub_category_name_E) === sNorm
      );

      setSelectedCategory(
        matchedC ? matchedC.category_name_E : ""
      );

      setSelectedSubCategory(
        matchedS ? matchedS.sub_category_name_E : ""
      );
    }
  }, [
    categories,
    subCategories,
    categoryFromUrl,
    subcategoryFromUrl,
  ]);

  const getLocalizedText = (key) =>
    isArabic
      ? safeStr(key.A || key.E)
      : safeStr(key.E || key.A);

  // Product filtering
  const filteredProducts = useMemo(() => {
    return products.filter((p) => {
      if (selectedBrand) {
        const brandOk =
          safeStr(p.product_brand_name) === selectedBrand;

        const capOk = selectedCapacity
          ? Array.isArray(p.product_capacity_array) &&
            p.product_capacity_array.some(
              (cap) =>
                normalizeString(cap) ===
                normalizeString(selectedCapacity)
            )
          : true;

        return brandOk && capOk;
      }

      const subOk = selectedSubCategory
        ? normalizeString(p.product_sub_category) ===
          normalizeString(selectedSubCategory)
        : true;

      const catOk = selectedCategory
        ? normalizeString(p.product_category) ===
          normalizeString(selectedCategory)
        : true;

      const capOk = selectedCapacity
        ? Array.isArray(p.product_capacity_array) &&
          p.product_capacity_array.some(
            (cap) =>
              normalizeString(cap) ===
              normalizeString(selectedCapacity)
          )
        : true;

      return subOk && catOk && capOk;
    });
  }, [
    products,
    selectedBrand,
    selectedCapacity,
    selectedCategory,
    selectedSubCategory,
  ]);

  // Shuffle products when filters change
  const shuffledProducts = useMemo(
    () => shuffleArray(filteredProducts),
    [filteredProducts]
  );

  // Pagination
  const totalPages = Math.ceil(
    shuffledProducts.length / PRODUCTS_PER_PAGE
  );

  const paginatedProducts = useMemo(() => {
    const startIndex =
      (currentPage - 1) * PRODUCTS_PER_PAGE;

    return shuffledProducts.slice(
      startIndex,
      startIndex + PRODUCTS_PER_PAGE
    );
  }, [shuffledProducts, currentPage]);

  // Return to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [
    selectedCategory,
    selectedSubCategory,
    selectedBrand,
    selectedCapacity,
  ]);

  // Ensure current page stays valid
  useEffect(() => {
    if (totalPages > 0 && currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [totalPages, currentPage]);

  const changePage = (page) => {
    if (page < 1 || page > totalPages) return;

    setCurrentPage(page);

    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  };

  // Pagination numbers
  const getPageNumbers = () => {
    if (totalPages <= 7) {
      return Array.from(
        { length: totalPages },
        (_, i) => i + 1
      );
    }

    const pages = [
      1,
      2,
      currentPage - 1,
      currentPage,
      currentPage + 1,
      totalPages - 1,
      totalPages,
    ]
      .filter((page) => page >= 1 && page <= totalPages)
      .sort((a, b) => a - b);

    const unique = [...new Set(pages)];
    const result = [];

    unique.forEach((page, index) => {
      if (index > 0 && page - unique[index - 1] > 1) {
        result.push(`dots-${index}`);
      }
      result.push(page);
    });

    return result;
  };

  return (
    <>
      {/* Page Header */}
      <section className="landingPages">
        <div className="container h-full flex flex-col gap-2 justify-center">
          <div className="text-[22px] sm:text-[28px] md:text-[35px] font-bold text-white">
            {t("Sub Categories")}
          </div>

          <div className="flex flex-wrap items-center gap-1 text-white text-sm sm:text-base">
            <Link to="/" className="hover:underline">
              {t("Home")}
            </Link>

            <span>-</span>

            <div>
              {subcategory || t("Sub Categories")}
            </div>
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="relative py-6 sm:py-10">
        <div className="container flex flex-col gap-8 sm:gap-12">
          {isLoading ? (
            <p className="text-center py-10">
              {t("Loading...")}
            </p>
          ) : (
            <div className="w-full flex flex-col gap-6 sm:gap-10">

              {/* Filters */}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">

                {/* Categories */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[#1485a6] font-bold text-sm sm:text-base">
                    {t("Categories")}
                  </label>

                  <select
                    className="w-full p-2 rounded-md outline-none border border-[#1485a6] bg-white/70 text-[#374151]"
                    value={selectedCategory}
                    onChange={(e) =>
                      setSelectedCategory(e.target.value)
                    }
                  >
                    <option value="">
                      {t("Show All Categories")}
                    </option>

                    {categories.map((item) => (
                      <option
                        key={item.category_id}
                        value={item.category_name_E}
                      >
                        {getLocalizedText({
                          A: item.category_name_A,
                          E: item.category_name_E,
                        })}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Sub Categories */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[#1485a6] font-bold text-sm sm:text-base">
                    {t("Sub Categories")}
                  </label>

                  <select
                    className="w-full p-2 rounded-md outline-none border border-[#1485a6] bg-white/70 text-[#374151]"
                    value={selectedSubCategory}
                    onChange={(e) =>
                      setSelectedSubCategory(e.target.value)
                    }
                  >
                    <option value="">
                      {t("Show All Sub Categories")}
                    </option>

                    {subCategories.map((item) => (
                      <option
                        key={item.sub_category_id}
                        value={item.sub_category_name_E}
                      >
                        {getLocalizedText({
                          A: item.sub_category_name_A,
                          E: item.sub_category_name_E,
                        })}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Brand */}
                <div className="flex flex-col gap-1.5">
                  <label className="text-[#1485a6] font-bold text-sm sm:text-base">
                    {t("Brand")}
                  </label>

                  <select
                    className="w-full p-2 rounded-md outline-none border border-[#1485a6] bg-white/70 text-[#374151]"
                    value={selectedBrand}
                    onChange={(e) =>
                      setSelectedBrand(e.target.value)
                    }
                  >
                    <option value="">
                      {t("Show All Brands")}
                    </option>

                    {brands.map((item) => (
                      <option
                        key={item.brand_id}
                        value={item.brand_name}
                      >
                        {item.brand_name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Capacity */}
                {normalizeString(selectedCategory) !== "airpods" &&
                  normalizeString(selectedCategory) !== "charger" && (
                    <div className="flex flex-col gap-1.5">
                      <label className="text-[#1485a6] font-bold text-sm sm:text-base">
                        {t("Capacity")}
                      </label>

                      <select
                        className="w-full p-2 rounded-md outline-none border border-[#1485a6] bg-white/70 text-[#374151]"
                        value={selectedCapacity}
                        onChange={(e) =>
                          setSelectedCapacity(e.target.value)
                        }
                      >
                        <option value="">
                          {t("Show All Capacities")}
                        </option>
                        <option value="64 GB">64 GB</option>
                        <option value="128 GB">128 GB</option>
                        <option value="256 GB">256 GB</option>
                        <option value="512 GB">512 GB</option>
                        <option value="1 T">1 T</option>
                      </select>
                    </div>
                  )}
              </div>

              {/* Products Grid */}
              <div
                className="
                  grid
                  grid-cols-2
                  md:grid-cols-3
                  xl:grid-cols-4
                  gap-2
                  sm:gap-4
                  items-stretch
                "
              >
                {paginatedProducts.length > 0 ? (
                  paginatedProducts.map((product, index) => {
                    const lang = i18n.language;

                    const productName = getLocalizedText({
                      A: product.product_name_A,
                      E: product.product_name_E,
                    });

                    const productDesc = getLocalizedText({
                      A: product.product_desc_A,
                      E: product.product_desc_E,
                    });

                    const descItems = splitDesc(
                      productDesc,
                      lang
                    ).slice(0, 2);

                    const price = toNum(
                      product.product_price,
                      0
                    );

                    const discountPct = Math.min(
                      100,
                      Math.max(
                        0,
                        toNum(
                          product.product_price_discount,
                          0
                        )
                      )
                    );

                    const priceAfterDiscount = (
                      price * (1 - discountPct / 100)
                    ).toFixed(2);

                    const inStock =
                      toNum(
                        product.product_stock_available,
                        0
                      ) > 0;

                    const inventoryClass = inStock
                      ? "text-[#18AB00]"
                      : "text-red-500";

                    const rawImage = safeStr(
                      product.product_main_img
                    );

                    const imgSrc = rawImage
                      ? /^https?:\/\//i.test(rawImage)
                        ? rawImage
                        : `${STORAGE_BASE}${rawImage.replace(
                            /^\/+/,
                            ""
                          )}`
                      : PLACEHOLDER;

                    const productUrl =
                      `/productdetails/${product.product_id ?? ""}`;

                    return (
                      <div
                        key={
                          product.product_id ??
                          `${product.product_name_E}-${index}`
                        }
                        className="
                          group cursor-pointer
                          flex flex-col
                          w-full min-w-0 h-full
                          rounded-md shadow-md
                          outline outline-[2px]
                          relative bg-white
                          overflow-hidden
                        "
                        style={{
                          outlineColor: "#eee",
                        }}
                      >
                        {/* Product Image */}
                        <Link to={productUrl}>
                          <div className="w-full relative">
                            <div className="w-full aspect-[4/3] overflow-hidden rounded-t-md">
                              <img
                                className="w-full h-full object-cover"
                                src={imgSrc}
                                alt={
                                  safeStr(
                                    product.product_name_E
                                  ) || "product"
                                }
                                loading="lazy"
                                onError={(e) => {
                                  e.currentTarget.onerror = null;
                                  e.currentTarget.src =
                                    PLACEHOLDER;
                                }}
                              />
                            </div>

                            {/* Discount */}
                            {discountPct > 0 && (
                              <div
                                className="
                                  dis-grid
                                  w-7 h-7
                                  sm:w-10 sm:h-10
                                  bg-[#1485a6]
                                  rounded-full
                                  text-white font-bold
                                  flex items-center
                                  justify-center
                                  absolute top-2 right-2
                                  text-[10px] sm:text-sm
                                "
                              >
                                {discountPct.toFixed(0)}%
                              </div>
                            )}
                          </div>
                        </Link>

                        <div className="h-[2px] bg-[#e5e7eb]" />

                        {/* Product Information */}
                        <div
                          className="
                            py-2 sm:py-3
                            flex flex-col
                            gap-2 sm:gap-3
                            px-2 sm:px-4
                            pb-12 sm:pb-14
                            flex-1 min-w-0
                          "
                        >
                          {/* Name + Status */}
                          <div className="flex flex-wrap items-start justify-between gap-1 sm:gap-2">
                            <div
                              className="
                                name-grid
                                text-[12px]
                                sm:text-[15px]
                                md:text-[16px]
                                font-bold
                                line-clamp-2
                                break-words
                                min-w-0
                              "
                            >
                              {productName}
                            </div>

                            {safeStr(product.product_status) && (
                              <div
                                className="
                                  new-grid
                                  min-w-[45px]
                                  sm:min-w-[56px]
                                  h-[19px]
                                  bg-[#1485a6]
                                  text-white capitalize
                                  font-bold
                                  rounded-tr-[100px]
                                  rounded-bl-[100px]
                                  flex items-center
                                  justify-center
                                  text-[9px] sm:text-[10px]
                                  px-2
                                "
                              >
                                {safeStr(
                                  product.product_status
                                )}
                              </div>
                            )}
                          </div>

                          {/* Description */}
                          <ul
                            className="
                              text-[10px] sm:text-[11px]
                              text-[#666666]
                              list-disc
                              pl-4
                              space-y-1
                              break-words
                            "
                          >
                            {descItems.map((desc, idx) => (
                              <li
                                key={idx}
                                className="leading-snug"
                              >
                                {desc.length > 75
                                  ? desc.substring(0, 75) +
                                    "..."
                                  : desc}
                              </li>
                            ))}
                          </ul>

                          {/* Price + Stock */}
                          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-1 mt-auto">
                            <div className="flex flex-col gap-1">
                              <span className="font-bold text-[12px] sm:text-[14px]">
                                {priceAfterDiscount}{" "}
                                <span>{t("JOD")}</span>
                              </span>

                              {discountPct > 0 && (
                                <span className="line-through text-red-500 text-[10px] sm:text-[12px]">
                                  {price.toFixed(2)}{" "}
                                  <span>{t("JOD")}</span>
                                </span>
                              )}
                            </div>

                            <div
                              className={`
                                font-bold capitalize
                                text-[11px] sm:text-[14px]
                                ${inventoryClass}
                              `}
                            >
                              {t(
                                inStock
                                  ? "In Stock"
                                  : "Out of Stock"
                              )}
                            </div>
                          </div>
                        </div>

                        {/* Add To Cart */}
                        <Link
                          to={productUrl}
                          className="
                            bg-[#1485a6]
                            flex p-2 sm:p-2.5
                            items-center
                            justify-center
                            gap-1.5
                            text-white font-bold
                            text-[10px] sm:text-sm
                            rounded-tl-[20px]
                            rounded-br-[20px]
                            absolute
                            bottom-0 left-0 right-0
                            opacity-100
                            md:opacity-0
                            md:group-hover:opacity-100
                            transition-opacity
                            duration-300
                          "
                        >
                          <CartProduct />
                          <span>
                            {t("Add To Cart")}
                          </span>
                        </Link>
                      </div>
                    );
                  })
                ) : (
                  <p className="col-span-full text-center py-10">
                    {t(
                      "No products match the selected filters."
                    )}
                  </p>
                )}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div
                  className="
                    flex flex-wrap
                    items-center
                    justify-center
                    gap-2
                    mt-4 sm:mt-8
                  "
                  dir="ltr"
                >
                  {/* Previous */}
                  <button
                    type="button"
                    disabled={currentPage === 1}
                    onClick={() =>
                      changePage(currentPage - 1)
                    }
                    className="
                      px-3 py-2
                      border rounded-md
                      bg-white
                      text-sm font-bold
                      disabled:opacity-40
                      disabled:cursor-not-allowed
                      hover:bg-[#1485a6]
                      hover:text-white
                      transition-colors
                    "
                  >
                    {isArabic ? "السابق" : "Previous"}
                  </button>

                  {/* Page Numbers */}
                  {getPageNumbers().map((page) =>
                    typeof page === "string" ? (
                      <span
                        key={page}
                        className="px-1 text-gray-500"
                      >
                        ...
                      </span>
                    ) : (
                      <button
                        key={page}
                        type="button"
                        onClick={() => changePage(page)}
                        className={`
                          min-w-[36px]
                          h-[36px]
                          px-2
                          border rounded-md
                          font-bold
                          text-sm
                          transition-colors
                          ${
                            currentPage === page
                              ? "bg-[#1485a6] text-white border-[#1485a6]"
                              : "bg-white text-black hover:bg-gray-100"
                          }
                        `}
                      >
                        {page}
                      </button>
                    )
                  )}

                  {/* Next */}
                  <button
                    type="button"
                    disabled={currentPage === totalPages}
                    onClick={() =>
                      changePage(currentPage + 1)
                    }
                    className="
                      px-3 py-2
                      border rounded-md
                      bg-white
                      text-sm font-bold
                      disabled:opacity-40
                      disabled:cursor-not-allowed
                      hover:bg-[#1485a6]
                      hover:text-white
                      transition-colors
                    "
                  >
                    {isArabic ? "التالي" : "Next"}
                  </button>
                </div>
              )}

              {/* Products Count */}
              {shuffledProducts.length > 0 && (
                <div className="text-center text-sm text-gray-500">
                  {isArabic
                    ? `عرض ${
                        (currentPage - 1) *
                          PRODUCTS_PER_PAGE +
                        1
                      } - ${Math.min(
                        currentPage * PRODUCTS_PER_PAGE,
                        shuffledProducts.length
                      )} من ${shuffledProducts.length} منتج`
                    : `Showing ${
                        (currentPage - 1) *
                          PRODUCTS_PER_PAGE +
                        1
                      } - ${Math.min(
                        currentPage * PRODUCTS_PER_PAGE,
                        shuffledProducts.length
                      )} of ${
                        shuffledProducts.length
                      } products`}
                </div>
              )}
            </div>
          )}
        </div>
      </section>
    </>
  );
}
